import { useState, useMemo, useEffect, useCallback } from 'react';
import { createDefaultStrategy, STRATEGY_COLORS, MACRS_RATES, SE_TAX, QBI, EBL_LIMITS, BONUS_DEP_RATES, CCORP_TAX_RATE, GROWTH_RATES, DEFAULT_UPTIME, DEFAULT_POOL_FEE, AVG_DAYS_PER_MONTH, TWO_POW_32, MAX_PROJECTION_MONTHS, IO_LOAN_SENTINEL, FALLBACK_HALVING_MONTHS, computeHalvingMonths, STATE_TAX_RATES, NON_CONFORMING_BONUS_DEP_STATES, TAX_YEAR, STANDARD_DEDUCTION, SECTION_179_LIMIT, NIIT, QUAL_DIVIDEND_RATES, SP500_START_PRICE, FEIE_EXCLUSION } from './lib/constants';
import { fetchMarketData, getCachedMarketData, FALLBACK_DEFAULTS } from './lib/marketDataService';
import { calculateStrategy as engineCalcStrategy, calculateDCAStrategies as engineCalcDCA, calculateTaxBenefits as engineCalcTax, computePlanEconomics } from './lib/engine';

// Module-level formatters (stable references, no state dependencies)
const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '$0';
  const neg = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${neg}$${(abs/1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${neg}$${(abs/1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${neg}$${(abs/1e3).toFixed(1)}K`;
  return `${neg}$${abs.toFixed(0)}`;
};
const fBTC = (v, decimals = 4) => (v !== undefined && v !== null && !isNaN(v)) ? v.toFixed(decimals) : '0.0000';

/* The five client archetypes. Each is a whole plan now, not a strategy
   nested inside one. PLAN_ARCHETYPES[0] seeds a fresh session; the rest
   are available as templates when creating a new plan. */
export const PLAN_ARCHETYPES = [
    {
      // LIQUID CLIENT, BILLS PAID BY THEIR BUSINESS: test 5 miners, ramp to 55
      // after proof of work. Outside funding covers bills as needed (shortfall
      // draw) so every mined sat is kept.
      ...createDefaultStrategy('1. Funded by Business', STRATEGY_COLORS[0]),
      startingMiners: 5, initialOpexCoverage: 'otherIncome',
      fundingMode: 'shortfall', otherIncome: 6000,
      expansions: [
        { month: 7, miners: 10, hashRate: 270, powerDraw: 3645, minerPrice: 5500, lifespan: 60, resaleValue: 500, opexCoverage: 'otherIncome', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
        { month: 13, miners: 20, hashRate: 290, powerDraw: 3500, minerPrice: 5800, lifespan: 60, resaleValue: 500, opexCoverage: 'otherIncome', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
        { month: 25, miners: 20, hashRate: 320, powerDraw: 3400, minerPrice: 6000, lifespan: 60, resaleValue: 500, opexCoverage: 'otherIncome', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
        { month: 61, miners: 15, hashRate: 380, powerDraw: 3200, minerPrice: 5500, lifespan: 60, resaleValue: 400, opexCoverage: 'otherIncome', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
      ],
    },
    {
      // LIQUID CLIENT, SELF-FUNDING: identical capital plan to #1, but mined
      // BTC pays the bills — zero ongoing outside cash. The head-to-head
      // partner for #1 in Compare Plans. Buffer-selling stays OFF: expansion
      // capex pulls cash negative (pocket contributions), and a cash floor
      // would liquidate every mined sat trying to refill it.
      ...createDefaultStrategy('2. Self-Funding Miner', STRATEGY_COLORS[1]),
      startingMiners: 5, initialOpexCoverage: 'btc', otherIncome: 0,
      expansions: [
        { month: 7, miners: 10, hashRate: 270, powerDraw: 3645, minerPrice: 5500, lifespan: 60, resaleValue: 500, opexCoverage: 'btc', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
        { month: 13, miners: 20, hashRate: 290, powerDraw: 3500, minerPrice: 5800, lifespan: 60, resaleValue: 500, opexCoverage: 'btc', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
        { month: 25, miners: 20, hashRate: 320, powerDraw: 3400, minerPrice: 6000, lifespan: 60, resaleValue: 500, opexCoverage: 'btc', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
        { month: 61, miners: 15, hashRate: 380, powerDraw: 3200, minerPrice: 5500, lifespan: 60, resaleValue: 400, opexCoverage: 'btc', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
      ],
    },
    {
      // CASH-FLOW LLC BUILDING A LONG-TERM TREASURY: $6K/mo of business cash
      // flow funds the bills and accumulates into yearly expansion tranches.
      // Never sells a sat.
      ...createDefaultStrategy('3. Treasury Builder', STRATEGY_COLORS[2]),
      startingMiners: 8, initialOpexCoverage: 'otherIncome',
      fundingMode: 'fixed', otherIncome: 6000,
      expansions: [
        { month: 13, miners: 6, hashRate: 290, powerDraw: 3500, minerPrice: 5800, lifespan: 60, resaleValue: 500, opexCoverage: 'otherIncome', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
        { month: 25, miners: 6, hashRate: 320, powerDraw: 3400, minerPrice: 6000, lifespan: 60, resaleValue: 500, opexCoverage: 'otherIncome', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
        { month: 37, miners: 8, hashRate: 350, powerDraw: 3300, minerPrice: 5800, lifespan: 60, resaleValue: 500, opexCoverage: 'otherIncome', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
        { month: 49, miners: 8, hashRate: 380, powerDraw: 3200, minerPrice: 5500, lifespan: 60, resaleValue: 450, opexCoverage: 'otherIncome', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
        { month: 61, miners: 10, hashRate: 400, powerDraw: 3100, minerPrice: 5200, lifespan: 60, resaleValue: 400, opexCoverage: 'otherIncome', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
      ],
    },
    {
      // TESTING THE WATERS: 3 miners, sell-to-cover, no commitments.
      // The Yr-5 fleet-retirement warning is intentional — a test, not a plan.
      ...createDefaultStrategy('4. Testing the Waters', STRATEGY_COLORS[4]),
      startingMiners: 3, initialOpexCoverage: 'btc', otherIncome: 0,
      autoSellForCashBuffer: true, cashBufferMin: 2000,
      expansions: [],
    },
    {
      // STAGGERED ENTRY: four planned tranches over three years. A $5K/mo
      // set-aside accumulates each year's tranche; mined BTC covers the bills.
      ...createDefaultStrategy('5. Staggered Entry', STRATEGY_COLORS[3]),
      startingMiners: 10, initialOpexCoverage: 'btc',
      fundingMode: 'fixed', otherIncome: 5000,
      expansions: [
        { month: 13, miners: 10, hashRate: 290, powerDraw: 3500, minerPrice: 5800, lifespan: 60, resaleValue: 500, opexCoverage: 'btc', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
        { month: 25, miners: 10, hashRate: 320, powerDraw: 3400, minerPrice: 6000, lifespan: 60, resaleValue: 500, opexCoverage: 'btc', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
        { month: 37, miners: 10, hashRate: 350, powerDraw: 3300, minerPrice: 5800, lifespan: 60, resaleValue: 500, opexCoverage: 'btc', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
        { month: 61, miners: 12, hashRate: 400, powerDraw: 3100, minerPrice: 5200, lifespan: 60, resaleValue: 400, opexCoverage: 'btc', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: false },
      ],
    },
];

export default function useDashboardState() {
  // ═══════ UI State ═══════
  const [showBenchmark, setShowBenchmark] = useState(true);

  // DCA Strategy Settings (amounts derived from mining cash flows — no independent schedule)
  const [dcaBtcEnabled, setDcaBtcEnabled] = useState(true);
  const [dcaBtcPriceScenario, setDcaBtcPriceScenario] = useState(2);

  const [dcaSpEnabled, setDcaSpEnabled] = useState(true);
  const [dcaSpReturn, setDcaSpReturn] = useState(10);

  // Tax Settings
  const [taxRate, setTaxRate] = useState(24);
  // Bonus depreciation: 100% through 2029 per OBBBA (July 2025), always on
  const useBonusDepreciation = true;
  const [showAfterTaxEconomics, setShowAfterTaxEconomics] = useState(true);

  // SE Tax Settings
  const [w2SSWages, setW2SSWages] = useState(0);
  const [otherSEIncome, setOtherSEIncome] = useState(150000);

  // Tax Structure Comparison Settings
  const [entityType, setEntityType] = useState('mmllc_foreign');
  const [scorpSalaryPercent, setScorpSalaryPercent] = useState(30);
  const [filingStatus, setFilingStatus] = useState('single');
  const [stateCode, setStateCode] = useState('NONE');
  const stateTaxRate = (STATE_TAX_RATES.find(s => s.code === stateCode) || STATE_TAX_RATES[0]).rate;
  const stateConformsBonus = stateCode === 'NONE' || !NON_CONFORMING_BONUS_DEP_STATES.has(stateCode);

  // QBI auto-applies for pass-through entities (LLC, S-Corp) — no manual toggle needed
  const qbiEnabled = entityType !== 'ccorp';

  // International Tax Settings
  const [ownerResidency, setOwnerResidency] = useState('us_resident');
  const [foreignTaxRegime, setForeignTaxRegime] = useState('none');
  const [llcManagementLocation, setLLCManagementLocation] = useState('us');
  const [incomeType, setIncomeType] = useState('active');

  // FEIE — IRC §911: US citizens abroad can exclude foreign earned income from federal income tax
  // FEIE does NOT exempt SE tax (IRC §1402). SE tax is always computed on full earnings.
  const feieApplies = ownerResidency === 'us_citizen_abroad';
  const totalEarnedIncome = (otherSEIncome || 0) + (w2SSWages || 0);
  const feieRemaining = Math.max(0, FEIE_EXCLUSION - totalEarnedIncome);
  // If FEIE covers all earned income + likely mining income, effective income tax rate is 0%
  // User's selected taxRate is their marginal bracket — only applies to income ABOVE FEIE
  const feieCoversIncome = feieApplies && totalEarnedIncome <= FEIE_EXCLUSION;
  // effectiveIncomeTaxRate is derived below (after projections) so the FEIE
  // exclusion can be blended against actual mining profit rather than applied
  // as an all-or-nothing cliff.

  // ═══════ Live Market Data ═══════
  // Use cached data for instant initial render, then fetch fresh in background
  const cachedMarket = getCachedMarketData();
  const initMarket = cachedMarket || FALLBACK_DEFAULTS;

  // Market settings (initialized from cache/API data)
  const [timeHorizon, setTimeHorizon] = useState(15);
  const [btcPrice, setBtcPrice] = useState(initMarket.btcPrice);
  const [priceScenario, setPriceScenario] = useState(3);
  const [electricityCost, setElectricityCost] = useState(0.08);
  const [hostingFixed, setHostingFixed] = useState(5);
  const [poolFee, setPoolFee] = useState(DEFAULT_POOL_FEE);
  const [uptimeFactor, setUptimeFactor] = useState(DEFAULT_UPTIME);
  const [blockReward, setBlockReward] = useState(initMarket.blockReward);
  const [difficulty, setDifficulty] = useState(initMarket.difficulty);
  const [difficultyGrowth, setDifficultyGrowth] = useState(initMarket.difficultyGrowth);
  const [startingTreasury, setStartingTreasury] = useState(0);
  const [startingCash, setStartingCash] = useState(50000);

  // Advisor identity for report covers/footers; survives reloads.
  const [advisorProfile, setAdvisorProfile] = useState(() => {
    try { return { name: '', title: '', firm: '', email: '', phone: '', ...JSON.parse(localStorage.getItem('minerops_advisor') || '{}') }; }
    catch { return { name: '', title: '', firm: '', email: '', phone: '' }; }
  });
  const updateAdvisorProfile = (patch) => {
    setAdvisorProfile(prev => {
      const next = { ...prev, ...patch };
      try { localStorage.setItem('minerops_advisor', JSON.stringify(next)); } catch { /* full/unavailable */ }
      return next;
    });
  };

  // Market data metadata
  const [marketDataMeta, setMarketDataMeta] = useState({
    source: cachedMarket?.source || null,
    fetchedAt: cachedMarket?.fetchedAt || null,
    loading: !cachedMarket, // loading if no cache on first render
    error: null,
    fromCache: !!cachedMarket,
    difficultyAdjustment: cachedMarket?.difficultyAdjustment || null,
    networkHashrateEH: initMarket.networkHashrateEH || null,
    blockHeight: cachedMarket?.blockHeight || null,
  });

  const refreshMarketData = useCallback(async (force = true) => {
    setMarketDataMeta(prev => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetchMarketData({ force });
      // Update market inputs with live data
      setBtcPrice(data.btcPrice);
      setDifficulty(data.difficulty);
      setBlockReward(data.blockReward);
      setDifficultyGrowth(data.difficultyGrowth);
      setMarketDataMeta({
        source: data.source,
        fetchedAt: data.fetchedAt || Date.now(),
        loading: false,
        error: null,
        fromCache: data.fromCache,
        stale: data.stale || false,
        difficultyAdjustment: data.difficultyAdjustment || null,
        networkHashrateEH: data.networkHashrateEH || null,
        blockHeight: data.blockHeight || null,
      });
    } catch (err) {
      setMarketDataMeta(prev => ({ ...prev, loading: false, error: err.message }));
    }
  }, []);

  // Fetch fresh data on mount (non-blocking)
  useEffect(() => {
    refreshMarketData(false); // false = use cache if fresh, else fetch
  }, [refreshMarketData]);

  const halvingMonths = useMemo(() => {
    const height = marketDataMeta?.blockHeight;
    return computeHalvingMonths(height);
  }, [marketDataMeta?.blockHeight]);
  const { ssWageBase, ssRate, medicareRate, netEarningsFactor } = SE_TAX;

  // ═══════ The plan's configuration ═══════
  // One plan = one configuration. Comparison happens between plans.
  const [strategy, setStrategy] = useState(() => structuredClone(PLAN_ARCHETYPES[0]));



  // ═══════ One plan, one configuration ═══════
  // Plans used to hold up to six strategies, which duplicated the plan-vs-plan
  // comparison one level down and left screens silently picking a strategy for
  // the reader. A plan is now a single configuration; comparison happens
  // between plans. `activeStrategy` is kept as the exported name so every
  // downstream section keeps reading the same thing.
  const activeStrategy = strategy;

  // ═══════ Handlers ═══════
  const updateStrategy = (updates) => {
    setStrategy(prev => ({ ...prev, ...updates }));
  };

  const updateStrategyExpansion = (expIndex, field, value) => {
    setStrategy(prev => {
      const expansions = [...prev.expansions];
      expansions[expIndex] = { ...expansions[expIndex], [field]: value };
      return { ...prev, expansions };
    });
  };

  // ═══════ Calculation Functions ═══════
  // Pure math lives in src/lib/engine.js — these wrappers bind current state.
  const engineParams = { timeHorizon, btcPrice, priceScenario, electricityCost, hostingFixed, poolFee, uptimeFactor, blockReward, difficulty, difficultyGrowth, startingTreasury, startingCash, halvingMonths };
  const calculateStrategy = (strategy) => engineCalcStrategy(strategy, engineParams);
  const calculateDCAStrategies = (miningResults, strategy) => engineCalcDCA(miningResults, strategy, { priceScenario, btcPrice, startingTreasury, dcaSpReturn, timeHorizon, dcaBtcEnabled, dcaSpEnabled });
  const calculateTaxBenefits = (totalCapEx, annualData) => engineCalcTax(totalCapEx, annualData, { effectiveIncomeTaxRate, stateTaxRate, useBonusDepreciation });

  // ═══════ Memoized Calculations ═══════
  // Local (synchronous) computation for instant UI
  const planCalculation = useMemo(
    () => ({ strategy, results: calculateStrategy(strategy) }),
    [strategy, timeHorizon, btcPrice, priceScenario, electricityCost, hostingFixed, poolFee, blockReward, difficulty, difficultyGrowth, startingTreasury, startingCash, uptimeFactor, halvingMonths]
  );

  const localDcaStrategies = useMemo(() => {
    if (!showBenchmark) return { btcDCA: null, spDCA: null };
    if (!planCalculation.results?.monthlyData) return { btcDCA: null, spDCA: null };
    return calculateDCAStrategies(planCalculation.results, strategy);
  }, [showBenchmark, planCalculation, strategy,
      dcaBtcEnabled, dcaSpEnabled, dcaSpReturn,
      btcPrice, priceScenario, timeHorizon, startingTreasury, startingCash]);

  // The JS engine is the single source of truth for projections. A server-side
  // Python port existed briefly but drifted from the audited JS math within days
  // (different NAV/ROI basis, broken DCA mirroring) — silently overriding correct
  // local numbers. Calculation stays client-side; the backend handles auth + persistence.
  const dcaStrategies = localDcaStrategies;

  // Kept as an array so charts, tables and reports keep one contract: series[0]
  // is this plan, the rest are benchmarks. It is no longer a list of user-editable
  // strategies — those are separate plans now.
  const displayCalculations = useMemo(() => {
    const calcs = [planCalculation];
    if (dcaStrategies.btcDCA) {
      calcs.push({ strategy: { name: dcaStrategies.btcDCA.name, color: dcaStrategies.btcDCA.color, isBenchmark: true, isDCA: true }, results: dcaStrategies.btcDCA });
    }
    if (dcaStrategies.spDCA) {
      calcs.push({ strategy: { name: dcaStrategies.spDCA.name, color: dcaStrategies.spDCA.color, isBenchmark: true, isDCA: true }, results: dcaStrategies.spDCA });
    }
    return calcs;
  }, [planCalculation, dcaStrategies]);

  const allCalculations = useMemo(() => [planCalculation], [planCalculation]);

  const activeCalc = planCalculation.results;

  // FEIE-blended effective federal rate. FEIE (§911) excludes up to the cap of
  // foreign EARNED income — mining profit above the remaining exclusion is still
  // taxed at the user's bracket. Blend instead of a binary 0%/full cliff.
  const effectiveIncomeTaxRate = useMemo(() => {
    if (!feieApplies || !feieCoversIncome) return taxRate;
    const years = Math.max(1, timeHorizon);
    const avgMiningProfit = (activeCalc?.annualData || [])
      .reduce((s, y) => s + Math.max(0, (y.btcMinedValue || 0) - (y.opEx || 0)), 0) / years;
    if (avgMiningProfit <= feieRemaining) return 0;
    return taxRate * (1 - feieRemaining / avgMiningProfit);
  }, [feieApplies, feieCoversIncome, feieRemaining, taxRate, activeCalc, timeHorizon]);

  const taxBenefits = useMemo(() => {
    if (!activeCalc) return null;
    return calculateTaxBenefits(activeCalc.totalCapEx || 0, activeCalc.annualData);

  }, [activeCalc, effectiveIncomeTaxRate, stateTaxRate, useBonusDepreciation]);

  const miningEconomics = useMemo(() => {
    if (!activeCalc || !activeCalc.totalBTCMined || activeCalc.totalBTCMined <= 0) return null;

    const totalOpEx = activeCalc.annualData?.reduce((sum, y) => sum + (y.opEx || 0), 0) || 0;
    // Use totalCapExPaid (excludes sunk miners) for acquisition economics
    const totalCapExPaid = activeCalc.totalCapExPaid || activeCalc.totalCapEx || 0;
    const totalCapEx = activeCalc.totalCapEx || 0;
    const isSunk = activeCalc.initialCapexIsSunk;
    const totalBTCMined = activeCalc.totalBTCMined;

    // Delegated to engine.computePlanEconomics so this screen, Compare and the
    // reports cannot drift. They already had: Compare showed the pre-tax
    // multiple while everything else showed after-tax, because each computed
    // its own. This block previously claimed to delegate and did not, which is
    // also why postTaxBreakEvenMonth never reached the Plan Overview.
    const econ = computePlanEconomics(activeCalc, {
      taxRate, stateCode, entityType, ownerResidency, w2SSWages, otherSEIncome,
      showAfterTaxEconomics,
    }, {
      btcPrice, timeHorizon,
      stateTaxRates: STATE_TAX_RATES,
      feieExclusion: FEIE_EXCLUSION,
    });
    if (!econ) return null;

    return { ...econ, totalCapEx, isSunk };
  }, [activeCalc, btcPrice, timeHorizon, taxRate, stateCode, entityType, ownerResidency, w2SSWages, otherSEIncome, showAfterTaxEconomics]);

  // ═══════ Equivalent DCA: Same Dollars, Same Timing ═══════
  // "If you took every dollar you spent on mining and DCA'd it into BTC or S&P 500 instead"
  const equivalentDCA = useMemo(() => {
    if (!showBenchmark) return null;
    if (!activeCalc?.monthlyData || activeCalc.monthlyData.length === 0) return null;
    const md = activeCalc.monthlyData;
    const totalMonths = md.length;
    const spAnnualReturn = dcaSpReturn / 100;

    // Starting capital: treasury BTC + cash are capital deployed into this strategy
    const startingCapitalValue = startingTreasury * btcPrice + (startingCash || 0);
    let totalCashDeployed = startingCapitalValue;
    let dcaBtcAccum = startingTreasury; // Start with same BTC holdings
    let spValue = 0;
    const monthlyOutflows = [];

    // S&P: starting capital compounds for the full period
    spValue += startingCapitalValue * Math.pow(1 + spAnnualReturn / 12, totalMonths);

    // Month 0: initial CapEx from cash (if NOT sunk)
    const initialCapexCash = activeCalc.initialCapexIsSunk ? 0 : (activeStrategy.startingMiners * activeStrategy.minerPrice);

    for (let i = 0; i < totalMonths; i++) {
      const d = md[i];
      // Cash leaving your pocket for mining this month: the external funding
      // draw (contribution or bill coverage) plus cash-funded CapEx. Bills paid
      // by selling mined BTC are not out-of-pocket.
      const pocketDraw = d.externalFundsDrawn ?? ((d.opexCoveredByOtherIncome || 0) + (d.loanPayment || 0));
      const grossCapex = d.monthCapex || d.capexThisMonth || 0;
      const loanProceeds = d.monthLoanProceeds || 0;
      const netCapexFromCash = Math.max(0, grossCapex - loanProceeds); // Only the cash-funded portion
      let cashOut = pocketDraw + netCapexFromCash;
      // Add initial CapEx in month 1
      if (i === 0) cashOut += initialCapexCash;

      totalCashDeployed += cashOut;
      monthlyOutflows.push({ month: d.month, cashOut, btcPrice: d.btcPrice });

      // DCA into BTC: buy at this month's spot price
      if (d.btcPrice > 0) {
        dcaBtcAccum += cashOut / d.btcPrice;
      }

      // DCA into S&P 500: each month's investment compounds for remaining months
      const monthsRemaining = totalMonths - i - 1;
      spValue += cashOut * Math.pow(1 + spAnnualReturn / 12, monthsRemaining);
    }

    const finalBtcPrice = md[totalMonths - 1]?.btcPrice || btcPrice;
    const dcaBtcValue = dcaBtcAccum * finalBtcPrice;

    // Mining results for comparison
    const miningBTC = activeCalc.totalBTCMined || 0;
    const miningTreasury = activeCalc.finalTreasury || 0;
    const miningNAV = activeCalc.finalNAV || 0;
    const miningCash = activeCalc.finalCash || 0;

    // Effective cost per BTC for the DCA path
    const dcaCostPerBTC = dcaBtcAccum > 0 ? totalCashDeployed / dcaBtcAccum : 0;

    // ── Tax Advantage: mining gets deductions DCA doesn't ──
    const depSavings = taxBenefits?.totalTaxSavings || 0;
    const combinedRate = (effectiveIncomeTaxRate + stateTaxRate) / 100;
    // OpEx is deductible as business expense
    const totalOpExForDeduction = md.reduce((s, d) => s + (d.totalOpEx || 0), 0);
    const opExTaxSavings = totalOpExForDeduction * combinedRate;
    const totalTaxSavings = depSavings + opExTaxSavings;
    // After-tax NAV: tax savings reduce cost, not inflate asset value
    // The savings are already partially reflected in cash via reduced tax payments
    const miningNAVAfterTax = miningNAV;
    // After-tax cost per BTC
    const miningGrossCost = (activeCalc.totalCapExPaid || 0) + totalOpExForDeduction;
    const miningAfterTaxCost = miningGrossCost - totalTaxSavings;
    const miningCostPerBTC = miningBTC > 0 ? miningGrossCost / miningBTC : 0;
    const miningAfterTaxCostPerBTC = miningBTC > 0 ? miningAfterTaxCost / miningBTC : 0;

    return {
      totalCashDeployed,
      // BTC DCA
      dcaBtcAccum,
      dcaBtcValue,
      dcaCostPerBTC,
      // S&P 500
      spValue,
      // Mining (for side-by-side)
      miningBTC,
      miningTreasury,
      miningNAV,
      miningCash,
      // Tax advantage (mining only)
      depreciationSavings: depSavings,
      opExTaxSavings,
      totalTaxSavings,
      miningNAVAfterTax,
      miningCostPerBTC,
      miningAfterTaxCostPerBTC,
      miningGrossCost,
      miningAfterTaxCost,
      // Deltas (pre-tax)
      btcAdvantage: miningTreasury - dcaBtcAccum,
      navVsDcaBtc: miningNAV - dcaBtcValue,
      navVsSp: miningNAV - spValue,
      // Deltas (after-tax)
      navVsDcaBtcAfterTax: miningNAVAfterTax - dcaBtcValue,
      navVsSpAfterTax: miningNAVAfterTax - spValue,
      monthlyOutflows,
      finalBtcPrice,
    };
  }, [showBenchmark, activeCalc, activeStrategy, btcPrice, dcaSpReturn, taxBenefits, effectiveIncomeTaxRate, stateTaxRate]);

  const operatorEconomics = useMemo(() => {
    if (!activeCalc) return null;

    const totalMiningRevenue = activeCalc.annualData?.reduce((sum, y) => sum + (y.btcMinedValue || 0), 0) || 0;
    const avgAnnualRevenue = totalMiningRevenue / timeHorizon;
    const totalPoolFees = activeCalc.annualData?.reduce((sum, y) => sum + (y.poolFees || 0), 0) || 0;
    const avgAnnualPoolFees = totalPoolFees / timeHorizon;
    const avgMiners = activeCalc.annualData?.reduce((sum, y) => sum + (y.minersEOY || 0), 0) / timeHorizon || activeStrategy.startingMiners;
    const totalOpEx = activeCalc.annualData?.reduce((sum, y) => sum + (y.opEx || 0), 0) || 0;
    const avgAnnualOpEx = totalOpEx / timeHorizon;
    const miningNetProfit = avgAnnualRevenue - avgAnnualOpEx;

    const seBaseOther = otherSEIncome * netEarningsFactor;
    const baseUsedPreMining = w2SSWages + seBaseOther;
    const ssRemaining = Math.max(0, ssWageBase - baseUsedPreMining);

    // ── Year-by-year SE tax computation ──
    // IRS Schedule SE combines ALL SE income. A mining loss (from depreciation)
    // offsets other SE income, reducing the total SE tax base.
    const depByYear = {};
    if (taxBenefits?.yearlyBreakdown) {
      taxBenefits.yearlyBreakdown.forEach(yb => { depByYear[yb.year] = yb.depreciation; });
    }
    const startYear = TAX_YEAR;

    const { additionalMedicareRate, additionalMedicareThreshold } = SE_TAX;
    const amtThreshold = filingStatus === 'mfj' ? additionalMedicareThreshold.mfj : additionalMedicareThreshold.single;

    // Compute combined SE tax on ALL SE income (other business + mining)
    // Mining profit can be negative (loss from depreciation), reducing the combined base
    const computeCombinedSETax = (miningProfit) => {
      const combinedIncome = otherSEIncome + miningProfit;
      const seBase = Math.max(0, combinedIncome) * netEarningsFactor;
      const ssAfterW2 = Math.max(0, ssWageBase - w2SSWages);
      const ssTaxable = Math.min(seBase, ssAfterW2);
      const ss = ssTaxable * ssRate;
      const medicare = seBase * medicareRate;
      // Additional Medicare Tax (0.9%) applies when total Medicare wages (W-2 + SE) exceed threshold
      // Per IRS Form 8959: threshold comparison uses ALL Medicare wages, not just SE
      const totalMedicareWages = w2SSWages + seBase;
      const amtBase = Math.max(0, totalMedicareWages - amtThreshold);
      // Only attribute SE's share of the excess (W-2 employer already withholds on their portion)
      const seShareOfExcess = seBase > 0 ? Math.min(amtBase, seBase) : 0;
      return ss + medicare + seShareOfExcess * additionalMedicareRate;
    };

    // Baseline: SE tax with NO mining at all (just other SE income)
    const baselineSETax = computeCombinedSETax(0);

    // Year-by-year combined SE tax at three tiers:
    // 1) Gross revenue only (no OpEx, no depreciation)
    // 2) After OpEx (revenue - opex)
    // 3) After OpEx + depreciation (revenue - opex - dep)
    let totalSETaxGross = 0;
    let totalSETaxAfterOpEx = 0;
    let totalSETaxAfterAll = 0;
    for (let y = 0; y < timeHorizon; y++) {
      const yearData = activeCalc.annualData?.[y];
      const yearRevenue = yearData?.btcMinedValue || 0;
      const yearOpEx = yearData?.opEx || 0;
      const yearDep = depByYear[startYear + y] || 0;
      totalSETaxGross += computeCombinedSETax(yearRevenue);
      totalSETaxAfterOpEx += computeCombinedSETax(yearRevenue - yearOpEx);
      totalSETaxAfterAll += computeCombinedSETax(yearRevenue - yearOpEx - yearDep);
    }

    // Mining's marginal SE tax = combined - baseline (can be negative if mining loss reduces other SE)
    const llcSETax = (totalSETaxAfterAll / timeHorizon) - baselineSETax;
    const llcSETaxNoDep = (totalSETaxAfterOpEx / timeHorizon) - baselineSETax;
    // SE base for display: use the no-dep profit since that's the steady-state
    const miningSEBase = Math.max(0, miningNetProfit) * netEarningsFactor;

    // SE tax saved by OpEx reducing the combined SE base
    const opExSETaxSavings = Math.max(0, (totalSETaxGross - totalSETaxAfterOpEx) / timeHorizon);
    // SE tax saved by depreciation reducing the combined SE base (on top of OpEx)
    const depreciationSETaxSavings = Math.max(0, llcSETaxNoDep - llcSETax);

    // ── Entity-specific SE / Payroll Tax ──
    const isPassive = incomeType === 'distributions_only';
    let miningSETax = 0;
    let scorpSalary = 0;
    let scorpEmployerPayroll = 0;
    let scorpEmployeePayroll = 0;
    let scorpDistribution = 0;
    let scorpPayrollSavings = 0;
    let ccorpTax = 0;
    let qbiDeduction = 0;
    let miningNIIT = 0;

    if (isPassive) {
      // Passive income: no SE/payroll tax — but Net Investment Income Tax
      // (IRC §1411, 3.8%) applies to passive income above the AGI threshold.
      miningSETax = 0;
      const niitThresh = filingStatus === 'mfj' ? NIIT.threshold.mfj : NIIT.threshold.single;
      const totalAGI = (otherSEIncome || 0) + (w2SSWages || 0) + Math.max(0, miningNetProfit);
      miningNIIT = NIIT.rate * Math.max(0, Math.min(Math.max(0, miningNetProfit), totalAGI - niitThresh));
    } else if (entityType === 'ccorp') {
      // C-Corp: 21% flat corporate tax, no SE tax on owner
      miningSETax = 0;
      ccorpTax = Math.max(0, miningNetProfit) * CCORP_TAX_RATE;
    } else if (entityType === 'scorp') {
      // S-Corp: W-2 salary + distributions (depreciation reduces profit base)
      // Average profit after depreciation for S-Corp salary calc
      let totalDepAdjProfit = 0;
      for (let y = 0; y < timeHorizon; y++) {
        const yearData = activeCalc.annualData?.[y];
        const yearRevenue = yearData?.btcMinedValue || 0;
        const yearOpEx = yearData?.opEx || 0;
        const yearDep = depByYear[startYear + y] || 0;
        totalDepAdjProfit += Math.max(0, yearRevenue - yearOpEx - yearDep);
      }
      const avgDepAdjProfit = totalDepAdjProfit / timeHorizon;
      scorpSalary = avgDepAdjProfit * (scorpSalaryPercent / 100);
      // S-Corp salary is W-2 income — SS wage base remaining after other W-2 wages
      // Other SE income uses its own SS allocation on Schedule SE (separate from W-2 track)
      const ssRemainForSalary = Math.max(0, SE_TAX.ssWageBase - w2SSWages);
      const ssTaxableSalary = Math.min(scorpSalary, ssRemainForSalary);
      // Employer: 6.2% SS + 1.45% Medicare
      scorpEmployerPayroll = ssTaxableSalary * 0.062 + scorpSalary * 0.0145;
      // Employee: 6.2% SS + 1.45% Medicare + Additional Medicare (0.9% above threshold)
      const amtThreshold = filingStatus === 'mfj' ? SE_TAX.additionalMedicareThreshold.mfj : SE_TAX.additionalMedicareThreshold.single;
      const scorpTotalW2 = w2SSWages + scorpSalary;
      const scorpAdditionalMedicare = Math.max(0, scorpSalary - Math.max(0, amtThreshold - w2SSWages)) * SE_TAX.additionalMedicareRate;
      scorpEmployeePayroll = ssTaxableSalary * 0.062 + scorpSalary * 0.0145 + scorpAdditionalMedicare;
      scorpDistribution = Math.max(0, avgDepAdjProfit - scorpSalary - scorpEmployerPayroll);
      const scorpFUTA = Math.min(scorpSalary, SE_TAX.futaWageBase) * SE_TAX.futaRate;
      miningSETax = scorpEmployerPayroll + scorpEmployeePayroll + scorpFUTA;
      scorpPayrollSavings = Math.max(0, llcSETax - miningSETax);
    } else {
      // LLC / Sole Prop / MMLLC: SE tax on depreciation-adjusted profit
      miningSETax = llcSETax;
    }

    // QBI deduction (Section 199A) — 20% for pass-through entities
    // QBI is based on net income after depreciation deductions
    const avgDepreciationPerYear = (taxBenefits?.yearlyBreakdown || [])
      .reduce((sum, yb) => sum + yb.depreciation, 0) / Math.max(1, timeHorizon);
    const miningNetProfitAfterDep = miningNetProfit - avgDepreciationPerYear;
    if (qbiEnabled && entityType !== 'ccorp') {
      const qbiIncome = entityType === 'scorp'
        ? scorpDistribution
        : Math.max(0, miningNetProfitAfterDep);
      // QBI phase-out: taxable income before QBI deduction (IRC §199A(e)(2))
      // Subtract 50% SE tax deduction (IRC §164(f)) and approximate standard deduction
      const qbiThresholdStart = filingStatus === 'mfj' ? QBI.phaseInStart.mfj : QBI.phaseInStart.single;
      const qbiRange = filingStatus === 'mfj' ? QBI.phaseInRange.mfj : QBI.phaseInRange.single;
      const totalSETaxForDeduction = Math.max(0, llcSETaxNoDep + baselineSETax);
      const seDeductionForQBI = totalSETaxForDeduction * 0.5;
      const standardDeduction = filingStatus === 'mfj' ? STANDARD_DEDUCTION.mfj : STANDARD_DEDUCTION.single;
      const grossIncome = Math.max(0, qbiIncome) + (otherSEIncome || 0) + w2SSWages;
      const taxableIncome = Math.max(0, grossIncome - seDeductionForQBI - standardDeduction);

      // UBIA = unadjusted basis immediately after acquisition (original cost of depreciable assets)
      const totalCapExForUBIA = activeCalc?.annualData?.reduce((sum, y) => sum + (y.capexThisYear || 0), 0) || 0;
      const w2Wages = entityType === 'scorp' ? scorpSalary : 0;
      // Greater of: 50% W-2 wages, OR 25% W-2 wages + 2.5% UBIA
      const qbiWageLimit = Math.max(w2Wages * 0.50, w2Wages * 0.25 + totalCapExForUBIA * 0.025);

      if (taxableIncome <= qbiThresholdStart) {
        qbiDeduction = qbiIncome * QBI.rate;
      } else if (taxableIncome >= qbiThresholdStart + qbiRange) {
        // Above phase-out: QBI limited to W-2/UBIA cap
        qbiDeduction = Math.min(qbiIncome * QBI.rate, qbiWageLimit);
      } else {
        // In phase-out range: reduce proportionally
        const excess = taxableIncome - qbiThresholdStart;
        const reductionFactor = excess / qbiRange;
        const fullQBI = qbiIncome * QBI.rate;
        const limitedQBI = Math.min(fullQBI, qbiWageLimit);
        qbiDeduction = fullQBI - (fullQBI - limitedQBI) * reductionFactor;
      }
    }

    const hostingAnnual = avgMiners * hostingFixed * 12;
    const electricityAnnual = avgAnnualOpEx - hostingAnnual - avgAnnualPoolFees;
    const deductibleOpEx = Math.max(0, electricityAnnual) + hostingAnnual + avgAnnualPoolFees;

    const afterTaxOpEx = { electricity: Math.max(0, electricityAnnual), hosting: hostingAnnual, poolFees: avgAnnualPoolFees, total: deductibleOpEx };
    // Total OpEx tax savings = income tax savings + SE tax savings from OpEx reducing profit
    const opexIncomeTaxSavings = deductibleOpEx * ((effectiveIncomeTaxRate + stateTaxRate) / 100);
    const opexTaxSavings = opexIncomeTaxSavings + opExSETaxSavings;
    const netOpExAfterTax = deductibleOpEx - opexTaxSavings;

    return {
      avgAnnualRevenue, miningNetProfit, avgAnnualPoolFees, avgMiners,
      ssWageBase: ssWageBase, baseUsedPreMining, ssRemaining, miningSEBase,
      llcSETax, llcSETaxNoDep, miningSETax, depreciationSETaxSavings, opExSETaxSavings,
      scorpSalary, scorpEmployerPayroll, scorpEmployeePayroll, scorpDistribution, scorpPayrollSavings,
      ccorpTax, qbiDeduction, isPassive, miningNIIT,
      afterTaxOpEx, opexTaxSavings, netOpExAfterTax
    };
  }, [activeCalc, activeStrategy, taxBenefits,
      w2SSWages, otherSEIncome, effectiveIncomeTaxRate, stateTaxRate, poolFee, hostingFixed, timeHorizon,
      ssWageBase, ssRate, medicareRate, netEarningsFactor, entityType, scorpSalaryPercent,
      incomeType, qbiEnabled, filingStatus]);

  const taxStructureComparison = useMemo(() => {
    if (!activeCalc || !miningEconomics || !taxBenefits) return null;

    const totalCapEx = miningEconomics.totalCapExPaid || miningEconomics.totalCapEx || 0;
    const totalCapExFull = miningEconomics.totalCapEx || 0;
    const totalOpEx = miningEconomics.totalOpEx || 0;
    const combinedTaxRate = effectiveIncomeTaxRate + stateTaxRate;

    const isNonResidentAlien = ownerResidency === 'nra_treaty' || ownerResidency === 'nra_no_treaty';
    const isUSCitizenAbroad = ownerResidency === 'us_citizen_abroad';
    const hasUKManagement = llcManagementLocation === 'uk';
    const isPortugalNHR = foreignTaxRegime === 'portugal_nhr';
    const isMMLC_Foreign = entityType === 'mmllc_foreign';

    // ── Depreciation: use taxBenefits which correctly handles per-year CapEx expansions ──
    // taxBenefits.yearlyBreakdown has { year, depreciation, taxSavings, cumulative } per year
    const depreciationTaxSavings = taxBenefits.totalTaxSavings || 0;
    const yearlyDepBreakdown = taxBenefits.yearlyBreakdown || [];

    // State non-conformity adjustment (some states don't allow bonus dep)
    // This is a TIMING difference, not permanent — state still allows full MACRS over 5 years.
    // Without bonus: year-1 deduction = 20% (MACRS). With bonus: 100%. The deferred 80% is
    // spread over years 2-6 per MACRS schedule. NPV of $1 over MACRS 5yr at 5% discount ≈ $0.92,
    // so deferral cost ≈ 8% of state tax on the deferred amount.
    const stateNonConformityAdjustment = !stateConformsBonus && useBonusDepreciation
      ? totalCapEx * (stateTaxRate / 100) * 0.08 : 0;
    const adjustedDepSavings = depreciationTaxSavings - stateNonConformityAdjustment;

    // OpEx deductions (electricity, hosting, pool fees are all deductible)
    const annualOpExDeduction = (operatorEconomics?.afterTaxOpEx?.total || 0);
    const totalOpExDeduction = annualOpExDeduction * timeHorizon;
    const opExTaxSavings = totalOpExDeduction * (combinedTaxRate / 100);

    // ── SE Tax (other SE income) ──
    const seBaseOther = otherSEIncome * netEarningsFactor;
    const ssRemainingForOther = Math.max(0, ssWageBase - w2SSWages);
    const ssTaxableOther = Math.min(seBaseOther, ssRemainingForOther);
    const annualOtherSSTax = ssTaxableOther * ssRate;
    const annualOtherMedicare = seBaseOther * medicareRate;
    const annualOtherSETax = annualOtherSSTax + annualOtherMedicare;

    // SE exemption logic
    const nraSEExempt = isNonResidentAlien;
    const allSEExempt = entityType === 'ccorp' || nraSEExempt;
    const effectiveOtherSETax = allSEExempt ? 0 : annualOtherSETax;
    const totalEffectiveOtherSETax = effectiveOtherSETax * timeHorizon;

    const seTaxExemptReason = allSEExempt
      ? (entityType === 'ccorp' ? 'C-Corp' : 'NRA exempt')
      : null;

    // ── QBI Deduction (Section 199A) — from operatorEconomics ──
    const qbiDeduction = operatorEconomics?.qbiDeduction || 0;
    const qbiTaxSavings = qbiDeduction * (combinedTaxRate / 100);

    // ── Mining SE / Payroll Tax (entity-aware, from operatorEconomics) ──
    // miningSETax can be negative when mining losses offset other SE income
    const annualMiningSETax = operatorEconomics?.miningSETax ?? 0;
    const annualMiningNIIT = operatorEconomics?.miningNIIT || 0;
    const totalMiningNIIT = annualMiningNIIT * timeHorizon;
    const totalMiningSETax = annualMiningSETax * timeHorizon;
    const isPassive = operatorEconomics?.isPassive || false;

    // ── C-Corp Tax ──
    const annualCCorpTax = operatorEconomics?.ccorpTax || 0;
    const totalCCorpTax = annualCCorpTax * timeHorizon;

    // ── S-Corp Payroll Savings vs LLC ──
    const annualScorpSavings = operatorEconomics?.scorpPayrollSavings || 0;
    const totalScorpSavings = annualScorpSavings * timeHorizon;

    // ── EBL (Excess Business Loss Limitation, IRC §461(l)) ──
    // EBL is a per-year test. Year 1 is most likely to trigger due to bonus depreciation.
    const eblLimit = filingStatus === 'mfj' ? EBL_LIMITS.mfj : EBL_LIMITS.single;
    const year1Data = activeCalc?.annualData?.[0];
    const year1Revenue = year1Data?.btcMinedValue || 0;
    const year1OpEx = year1Data?.opEx || 0;
    const year1Capex = year1Data?.capexThisYear || 0;
    const bonusDepYear1 = useBonusDepreciation ? year1Capex * (BONUS_DEP_RATES[TAX_YEAR] || 0) : 0;
    // Year-1 business loss = revenue - opex - bonus depreciation (can be deeply negative)
    const year1BusinessLoss = Math.max(0, -(year1Revenue - year1OpEx - bonusDepYear1));
    const eblExcess = Math.max(0, year1BusinessLoss - eblLimit);
    const eblApplies = eblExcess > 0;

    // ── Passive Activity Warning ──
    const passiveActivityWarning = isPassive && adjustedDepSavings > 0;

    // Passive investors: depreciation losses are suspended (IRC §469)
    const passiveSuspended = isPassive;
    // EBL: excess business loss over the limit is deferred as NOL carryforward, not usable in year 1
    const eblDeferredTaxSavings = eblApplies ? eblExcess * (combinedTaxRate / 100) : 0;
    const effectiveDepSavings = passiveSuspended ? 0 : Math.max(0, adjustedDepSavings - eblDeferredTaxSavings);
    // SE tax savings from depreciation and OpEx reducing the profit base (year-by-year)
    const depSETaxSavings = (passiveSuspended || entityType === 'ccorp') ? 0
      : (operatorEconomics?.depreciationSETaxSavings || 0) * timeHorizon;
    const opExSETaxSavings = (passiveSuspended || entityType === 'ccorp') ? 0
      : (operatorEconomics?.opExSETaxSavings || 0) * timeHorizon;
    const totalMiningTaxBenefit = effectiveDepSavings + depSETaxSavings + opExTaxSavings + opExSETaxSavings + qbiTaxSavings - totalMiningNIIT;

    // What DCA would cost for the same BTC (no deductions)
    const totalBTCMined = miningEconomics.totalBTCMined || 0;
    const avgSpotPrice = miningEconomics.avgSpotPrice || 0;
    const dcaCost = totalBTCMined * avgSpotPrice;
    const grossMiningCost = totalCapEx + totalOpEx;
    const netMiningCost = grossMiningCost - totalMiningTaxBenefit;
    const miningVsDCASavings = dcaCost - netMiningCost;

    // ── Entity Comparison: LLC vs S-Corp vs C-Corp ──
    // Entity comparison uses steady-state mining profit (no depreciation) for apples-to-apples
    const miningProfit = operatorEconomics?.miningNetProfit || 0;
    const totalCapExForUBIA = miningEconomics?.totalCapEx || 0;

    // Shared QBI calculator with proper phase-out and UBIA
    const computeQBI = (qbiIncome, w2Wages, seTaxForDeduction = 0) => {
      if (!qbiEnabled) return 0;
      const qbiThresholdStart = filingStatus === 'mfj' ? QBI.phaseInStart.mfj : QBI.phaseInStart.single;
      const qbiRange = filingStatus === 'mfj' ? QBI.phaseInRange.mfj : QBI.phaseInRange.single;
      const standardDeduction = filingStatus === 'mfj' ? STANDARD_DEDUCTION.mfj : STANDARD_DEDUCTION.single;
      const seDeduction = seTaxForDeduction * 0.5; // IRC §164(f): 50% of SE tax deductible
      const grossIncome = Math.max(0, qbiIncome) + (otherSEIncome || 0) + w2SSWages;
      const taxableIncome = Math.max(0, grossIncome - seDeduction - standardDeduction);
      const fullQBI = qbiIncome * QBI.rate;
      const qbiWageLimit = Math.max(w2Wages * 0.50, w2Wages * 0.25 + totalCapExForUBIA * 0.025);
      if (taxableIncome <= qbiThresholdStart) return fullQBI;
      if (taxableIncome >= qbiThresholdStart + qbiRange) return Math.min(fullQBI, qbiWageLimit);
      const reductionFactor = (taxableIncome - qbiThresholdStart) / qbiRange;
      return fullQBI - (fullQBI - Math.min(fullQBI, qbiWageLimit)) * reductionFactor;
    };

    // LLC — only MINING SE tax feeds the 50% deduction here; SE tax on other
    // income is identical across all three entity scenarios and cancels out.
    const llcSETaxForEntity = operatorEconomics?.llcSETaxNoDep ?? 0;
    const llcSETax = Math.max(0, llcSETaxForEntity);
    const llcQBI = computeQBI(Math.max(0, miningProfit), 0, llcSETax);
    const seDeduction = llcSETax * 0.5; // IRC §164(f): 50% of SE tax deductible from AGI
    const llcIncomeTax = Math.max(0, miningProfit - seDeduction - llcQBI) * (combinedTaxRate / 100);
    const llcTotalTax = llcIncomeTax + llcSETax;

    // S-Corp (uses steady-state profit before depreciation for apples-to-apples entity comparison;
    // operatorEconomics uses depreciation-adjusted profit for actual S-Corp salary reporting)
    const scorpSal = Math.max(0, miningProfit) * (scorpSalaryPercent / 100);
    const scorpSSRemain = Math.max(0, ssWageBase - w2SSWages);
    const scorpSSTaxable = Math.min(scorpSal, scorpSSRemain);
    const scorpERPayroll = scorpSSTaxable * 0.062 + scorpSal * 0.0145;
    const scorpAMTThreshold = filingStatus === 'mfj' ? SE_TAX.additionalMedicareThreshold.mfj : SE_TAX.additionalMedicareThreshold.single;
    const scorpAddlMedicare = Math.max(0, scorpSal - Math.max(0, scorpAMTThreshold - w2SSWages)) * SE_TAX.additionalMedicareRate;
    const scorpEEPayroll = scorpSSTaxable * 0.062 + scorpSal * 0.0145 + scorpAddlMedicare;
    const scorpFUTA = Math.min(scorpSal, SE_TAX.futaWageBase) * SE_TAX.futaRate;
    const scorpDist = Math.max(0, miningProfit - scorpSal - scorpERPayroll);
    const scorpQBI = computeQBI(scorpDist, scorpSal, 0);
    const scorpIncomeTax = Math.max(0, scorpSal + scorpDist - scorpQBI) * (combinedTaxRate / 100);
    const scorpTotalTax = scorpIncomeTax + scorpERPayroll + scorpEEPayroll + scorpFUTA;

    // C-Corp: federal corp tax + state corp tax + qualified dividend tax (federal + state + NIIT)
    const ccorpCorpTax = Math.max(0, miningProfit) * CCORP_TAX_RATE;
    const ccorpStateCorpTax = Math.max(0, miningProfit) * (stateTaxRate / 100);
    const ccorpAfterTaxProfit = Math.max(0, miningProfit) - ccorpCorpTax - ccorpStateCorpTax;
    // Qualified dividend rate based on FEDERAL bracket only (not combined with state)
    const qualDivRate = taxRate >= 37 ? QUAL_DIVIDEND_RATES.high : taxRate <= 12 ? QUAL_DIVIDEND_RATES.low : QUAL_DIVIDEND_RATES.mid;
    const niitThreshold = filingStatus === 'mfj' ? NIIT.threshold.mfj : NIIT.threshold.single;
    const totalAGI = (otherSEIncome || 0) + w2SSWages + ccorpAfterTaxProfit;
    const niitRate = totalAGI > niitThreshold ? NIIT.rate : 0;
    // Dividends taxed at federal qualified rate + NIIT + state ordinary income rate
    const ccorpDividendTax = ccorpAfterTaxProfit * (qualDivRate + niitRate + stateTaxRate / 100);
    const ccorpTotalTax = ccorpCorpTax + ccorpStateCorpTax + ccorpDividendTax;

    const entityComparison = {
      llc: { incomeTax: llcIncomeTax, seTax: llcSETax, qbiSavings: llcQBI * (combinedTaxRate / 100), total: llcTotalTax, label: 'LLC / Sole Prop' },
      scorp: { incomeTax: scorpIncomeTax, payrollTax: scorpERPayroll + scorpEEPayroll + scorpFUTA, salary: scorpSal, distribution: scorpDist, qbiSavings: scorpQBI * (combinedTaxRate / 100), total: scorpTotalTax, savingsVsLLC: llcTotalTax - scorpTotalTax, label: 'S-Corp' },
      ccorp: { corpTax: ccorpCorpTax + ccorpStateCorpTax, dividendTax: ccorpDividendTax, rate: `${(21 + stateTaxRate).toFixed(1)}% corp + ${((qualDivRate + niitRate + stateTaxRate / 100) * 100).toFixed(1)}% div`, total: ccorpTotalTax, label: 'C-Corp' },
    };

    const portugalNHRBenefit = isPortugalNHR && hasUKManagement ? {
      applicable: true, foreignSourceExempt: true, distributionsTaxFree: true,
      notes: 'UK-managed LLC distributions may be exempt under Portugal NHR if UK has right to tax under DTA'
    } : { applicable: false };

    return {
      // Mining tax benefit breakdown
      totalCapEx,
      depreciationTaxSavings: effectiveDepSavings,
      depreciationSETaxSavings: depSETaxSavings,
      yearlyDepBreakdown,
      stateNonConformityAdjustment,
      opExTaxSavings,
      opExSETaxSavings,
      totalMiningTaxBenefit,
      // QBI
      qbiDeduction,
      qbiTaxSavings,
      // Mining SE / Payroll (entity-aware)
      annualMiningSETax,
      totalMiningSETax,
      annualMiningNIIT,
      totalMiningNIIT,
      isPassive,
      // C-Corp
      annualCCorpTax,
      totalCCorpTax,
      // S-Corp savings
      annualScorpSavings,
      totalScorpSavings,
      // EBL
      eblLimit,
      eblExcess,
      eblApplies,
      passiveActivityWarning,
      passiveSuspended,
      suspendedLosses: passiveSuspended ? adjustedDepSavings : 0,
      // Entity comparison
      entityComparison,
      // SE tax on other income
      annualOtherSETax: effectiveOtherSETax,
      totalOtherSETax: totalEffectiveOtherSETax,
      seTaxExemptReason,
      // Combined payroll/SE tax (whole business)
      annualTotalPayrollTax: annualMiningSETax + effectiveOtherSETax + annualCCorpTax,
      totalPayrollTax: totalMiningSETax + totalEffectiveOtherSETax + totalCCorpTax,
      // Net benefit
      netMiningCost,
      grossMiningCost,
      dcaCost,
      miningVsDCASavings,
      totalBTCMined,
      avgSpotPrice,
      // International
      international: {
        ownerResidency, isNonResidentAlien, isUSCitizenAbroad, hasUKManagement,
        isPortugalNHR, isMMLC_Foreign, seTaxExemptReason,
        portugalNHRBenefit, incomeType
      },
      inputs: { combinedTaxRate, timeHorizon, entityType, stateConformsBonus, useBonusDepreciation, filingStatus }
    };

  }, [activeCalc, miningEconomics, taxBenefits, operatorEconomics, effectiveIncomeTaxRate, stateTaxRate, useBonusDepreciation,
      stateConformsBonus, entityType, w2SSWages, otherSEIncome,
      timeHorizon, filingStatus, qbiEnabled, scorpSalaryPercent,
      ownerResidency, foreignTaxRegime, llcManagementLocation, incomeType]);

  const comparisonData = useMemo(() => {
    const maxMonths = Math.min(timeHorizon * 12, MAX_PROJECTION_MONTHS);
    const data = [];
    for (let m = 1; m <= maxMonths; m++) {
      const point = { month: m };
      displayCalculations.forEach((calc, i) => {
        const md = calc.results.monthlyData[m - 1];
        if (md) {
          point[`nav_${i}`] = md.nav;
          point[`treasury_${i}`] = md.treasuryBTC;
          point[`roi_${i}`] = md.roi;
          point[`btcKept_${i}`] = md.btcKept;
          point[`cash_${i}`] = md.cash;
        }
      });
      data.push(point);
    }
    return data;
  }, [displayCalculations, timeHorizon]);

  const currentStats = activeCalc?.current || {};

  const sensitivityAnalysis = useMemo(() => {
    if (!activeCalc || !activeCalc.finalNAV) return null;
    const baseNAV = activeCalc.finalNAV;
    const baseTreasury = activeCalc.finalTreasury;
    const finalCash = activeCalc.finalCash || 0;
    const finalPrice = activeCalc.finalBTCPrice || btcPrice;

    // Match the base NAV's debt-adjusted basis, else deltas overstate by outstanding debt
    const finalDebt = activeCalc.monthlyData?.[activeCalc.monthlyData.length - 1]?.outstandingDebt || 0;
    const pricePlus20 = baseTreasury * (finalPrice * 1.2) + finalCash - finalDebt;
    const priceMinus20 = baseTreasury * (finalPrice * 0.8) + finalCash - finalDebt;

    const btcMinedBase = activeCalc.totalBTCMined || 0;
    const btcKeptBase = activeCalc.totalBTCKept || 0;
    const btcSoldBase = activeCalc.totalBTCSold || 0;
    // Scale mined BTC inversely with difficulty. Kept/sold ratio stays constant.
    const keepRatio = btcMinedBase > 0 ? btcKeptBase / btcMinedBase : 0;
    const sellRatio = btcMinedBase > 0 ? btcSoldBase / btcMinedBase : 0;
    const diffPlus20BTC = btcMinedBase * (1 / 1.2);
    const diffMinus20BTC = btcMinedBase * (1 / 0.8);
    // Treasury = starting + kept (scaled). Cash adjusts for sold BTC revenue delta.
    const diffPlus20Treasury = startingTreasury + diffPlus20BTC * keepRatio;
    const diffMinus20Treasury = startingTreasury + diffMinus20BTC * keepRatio;
    const soldRevenueDeltaPlus = (diffPlus20BTC * sellRatio - btcSoldBase) * finalPrice;
    const soldRevenueDeltaMinus = (diffMinus20BTC * sellRatio - btcSoldBase) * finalPrice;
    const diffPlus20NAV = diffPlus20Treasury * finalPrice + finalCash + soldRevenueDeltaPlus - finalDebt;
    const diffMinus20NAV = diffMinus20Treasury * finalPrice + finalCash + soldRevenueDeltaMinus - finalDebt;

    const totalMonths = activeCalc.monthlyData?.length || 120;
    const avgPowerKWPerMonth = activeCalc.monthlyData
      ? activeCalc.monthlyData.reduce((s,d) => s + (d.totalPowerW || 0), 0) / totalMonths * 24 * 30.4375 / 1000
      : 0;
    const elecDeltaPerMonth = avgPowerKWPerMonth * 0.02;
    const elecPlus2NAV = baseNAV - (elecDeltaPerMonth * totalMonths);
    const elecMinus2NAV = baseNAV + (elecDeltaPerMonth * totalMonths);

    return {
      base: { nav: baseNAV, label: 'Base Case' },
      scenarios: [
        { label: 'BTC Price +20%', nav: pricePlus20, delta: pricePlus20 - baseNAV, color: '#16A34A' },
        { label: 'BTC Price −20%', nav: priceMinus20, delta: priceMinus20 - baseNAV, color: '#DC2626' },
        { label: 'Difficulty +20%', nav: diffPlus20NAV, delta: diffPlus20NAV - baseNAV, color: '#DC2626' },
        { label: 'Difficulty −20%', nav: diffMinus20NAV, delta: diffMinus20NAV - baseNAV, color: '#16A34A' },
        { label: 'Electricity +$0.02', nav: elecPlus2NAV, delta: elecPlus2NAV - baseNAV, color: '#DC2626' },
        { label: 'Electricity −$0.02', nav: elecMinus2NAV, delta: elecMinus2NAV - baseNAV, color: '#16A34A' },
      ]
    };
  }, [activeCalc, btcPrice]);

  const waterfallData = useMemo(() => {
    if (!activeCalc?.annualData) return [];
    return activeCalc.annualData.map(r => ({
      year: `Yr ${r.year}`, revenue: r.revenue || 0,
      otherIncome: r.loanProceedsThisYear || 0,
      opex: -(r.opEx || 0),
      loanPmt: -(r.loanPayments || 0),
      resale: r.resaleProceeds || 0,
      capex: -(r.capexThisYear || 0),
      net: r.netProfit || 0,
    }));
  }, [activeCalc]);

  // ═══════ DCA vs Mining Tax-Aware Comparison ═══════
  const dcaVsMiningComparison = useMemo(() => {
    if (!activeCalc || !miningEconomics) return null;
    const totalBTCMined = miningEconomics.totalBTCMined;
    if (totalBTCMined <= 0) return null;

    const totalCapEx = miningEconomics.totalCapExPaid;
    const totalOpEx = miningEconomics.totalOpEx;
    const grossMiningCost = totalCapEx + totalOpEx;
    const grossCostPerBTC = grossMiningCost / totalBTCMined;
    const avgSpotPrice = miningEconomics.avgSpotPrice;
    const dcaTotalCost = totalBTCMined * avgSpotPrice;

    // SE tax savings from depreciation + OpEx (constant regardless of income tax rate)
    const depSESavings = (taxStructureComparison?.depreciationSETaxSavings || 0);
    const opExSESavings = (taxStructureComparison?.opExSETaxSavings || 0);

    // OBBBA (July 2025) restored 100% bonus depreciation permanently for property acquired after Jan 19, 2025
    const bonusDepRates = { 2024: 0.60, 2025: 1.00, 2026: 1.00, 2027: 1.00 };
    const section179Limit = SECTION_179_LIMIT;
    const startYear = TAX_YEAR;

    const taxRateAnalysis = [];
    for (let rate = 0; rate <= 50; rate += 2) {
      const combinedRate = rate / 100;
      const bonusRate = bonusDepRates[startYear] || 0;
      // Match calculateTaxBenefits: bonus depreciation + MACRS on remaining basis
      const bonusDepAmount = totalCapEx * bonusRate;
      const remainingBasis = totalCapEx - bonusDepAmount;
      let macrsDepreciation = 0;
      MACRS_RATES.forEach(r => { macrsDepreciation += remainingBasis * r; });
      const totalDepreciation = bonusDepAmount + macrsDepreciation;
      const totalDeductions = totalDepreciation + totalOpEx;
      // Income tax savings + SE tax savings from depreciation and OpEx
      const taxSavings = totalDeductions * combinedRate + depSESavings + opExSESavings;
      const netMiningCost = grossMiningCost - taxSavings;
      const miningAdvantage = dcaTotalCost - netMiningCost;
      taxRateAnalysis.push({
        taxRate: rate, netMiningCost, netCostPerBTC: netMiningCost / totalBTCMined,
        dcaCost: dcaTotalCost, dcaCostPerBTC: avgSpotPrice, taxSavings, miningAdvantage,
        miningIsCheaper: netMiningCost < dcaTotalCost,
      });
    }

    const noIncentivesAnalysis = [];
    for (let rate = 0; rate <= 50; rate += 2) {
      const combinedRate = rate / 100;
      let macrsOnly = 0;
      MACRS_RATES.forEach(r => { macrsOnly += totalCapEx * r; });
      const taxSavingsMin = (macrsOnly + totalOpEx) * combinedRate;
      const netCostMin = grossMiningCost - taxSavingsMin;
      noIncentivesAnalysis.push({ taxRate: rate, netMiningCost: netCostMin, miningIsCheaper: netCostMin < dcaTotalCost });
    }

    const findBreakeven = (analysis) => {
      for (let i = 0; i < analysis.length - 1; i++) {
        const curr = analysis[i], next = analysis[i + 1];
        if (!curr.miningIsCheaper && next.miningIsCheaper) {
          const currDelta = curr.netMiningCost - dcaTotalCost;
          const nextDelta = next.netMiningCost - dcaTotalCost;
          if (currDelta !== nextDelta) return curr.taxRate + (currDelta / (currDelta - nextDelta)) * 2;
        }
      }
      return null;
    };

    const breakevenTaxRate = findBreakeven(taxRateAnalysis);
    const breakevenNoIncentives = findBreakeven(noIncentivesAnalysis);
    const miningAlwaysCheaper = taxRateAnalysis[0]?.miningIsCheaper === true;
    const miningNeverCheaper = taxRateAnalysis[taxRateAnalysis.length - 1]?.miningIsCheaper === false;
    const miningAlwaysCheaperNoIncentives = noIncentivesAnalysis[0]?.miningIsCheaper === true;
    const miningNeverCheaperNoIncentives = noIncentivesAnalysis[noIncentivesAnalysis.length - 1]?.miningIsCheaper === false;

    const userRate = effectiveIncomeTaxRate + stateTaxRate;
    const userAnalysis = taxRateAnalysis.reduce((closest, a) =>
      Math.abs(a.taxRate - userRate) < Math.abs(closest.taxRate - userRate) ? a : closest
    );

    return {
      totalBTCMined, grossMiningCost, grossCostPerBTC, dcaTotalCost, avgSpotPrice,
      taxRateAnalysis, noIncentivesAnalysis, breakevenTaxRate, breakevenNoIncentives,
      miningAlwaysCheaper, miningNeverCheaper, miningAlwaysCheaperNoIncentives, miningNeverCheaperNoIncentives,
      userAnalysis, userTaxRate: userRate, bonusDepRate: bonusDepRates[startYear] || 0, startYear,
    };
  }, [activeCalc, miningEconomics, effectiveIncomeTaxRate, stateTaxRate, MACRS_RATES, taxStructureComparison]);

  // ═══════ Plan Persistence: snapshot/restore the full app configuration ═══════
  const serializePlan = useCallback(() => ({
    version: 2,
    strategy, showBenchmark,
    market: { timeHorizon, btcPrice, priceScenario, electricityCost, hostingFixed, poolFee, uptimeFactor, blockReward, difficulty, difficultyGrowth, startingTreasury, startingCash },
    dca: { dcaBtcEnabled, dcaSpEnabled, dcaSpReturn },
    tax: { taxRate, stateCode, entityType, scorpSalaryPercent, filingStatus, ownerResidency, foreignTaxRegime, llcManagementLocation, incomeType, w2SSWages, otherSEIncome, showAfterTaxEconomics },
  }), [strategy, showBenchmark, timeHorizon, btcPrice, priceScenario, electricityCost, hostingFixed, poolFee, uptimeFactor, blockReward, difficulty, difficultyGrowth, startingTreasury, startingCash, dcaBtcEnabled, dcaSpEnabled, dcaSpReturn, taxRate, stateCode, entityType, scorpSalaryPercent, filingStatus, ownerResidency, foreignTaxRegime, llcManagementLocation, incomeType, w2SSWages, otherSEIncome, showAfterTaxEconomics]);

  const loadPlanData = useCallback((d) => {
    if (!d) return;
    if (d.strategy) {
      setStrategy(d.strategy);
    } else if (Array.isArray(d.strategies) && d.strategies.length) {
      // v1 plan: held several strategies with one marked active. Load the one
      // that was active — the others are recoverable from the stored snapshot
      // and are split out by scripts/split-legacy-plans.py.
      const idx = Number.isInteger(d.activeStrategyIndex) ? d.activeStrategyIndex : 0;
      setStrategy(d.strategies[idx] || d.strategies[0]);
    }
    if (typeof d.showBenchmark === 'boolean') setShowBenchmark(d.showBenchmark);
    const m = d.market || {};
    if (m.timeHorizon != null) setTimeHorizon(m.timeHorizon);
    if (m.btcPrice != null) setBtcPrice(m.btcPrice);
    if (m.priceScenario != null) setPriceScenario(m.priceScenario);
    if (m.electricityCost != null) setElectricityCost(m.electricityCost);
    if (m.hostingFixed != null) setHostingFixed(m.hostingFixed);
    if (m.poolFee != null) setPoolFee(m.poolFee);
    if (m.uptimeFactor != null) setUptimeFactor(m.uptimeFactor);
    if (m.blockReward != null) setBlockReward(m.blockReward);
    if (m.difficulty != null) setDifficulty(m.difficulty);
    if (m.difficultyGrowth != null) setDifficultyGrowth(m.difficultyGrowth);
    if (m.startingTreasury != null) setStartingTreasury(m.startingTreasury);
    if (m.startingCash != null) setStartingCash(m.startingCash);
    const dc = d.dca || {};
    if (typeof dc.dcaBtcEnabled === 'boolean') setDcaBtcEnabled(dc.dcaBtcEnabled);
    if (typeof dc.dcaSpEnabled === 'boolean') setDcaSpEnabled(dc.dcaSpEnabled);
    if (dc.dcaSpReturn != null) setDcaSpReturn(dc.dcaSpReturn);
    const t = d.tax || {};
    if (t.taxRate != null) setTaxRate(t.taxRate);
    if (t.stateCode != null) setStateCode(t.stateCode);
    if (t.entityType != null) setEntityType(t.entityType);
    if (t.scorpSalaryPercent != null) setScorpSalaryPercent(t.scorpSalaryPercent);
    if (t.filingStatus != null) setFilingStatus(t.filingStatus);
    if (t.ownerResidency != null) setOwnerResidency(t.ownerResidency);
    if (t.foreignTaxRegime != null) setForeignTaxRegime(t.foreignTaxRegime);
    if (t.llcManagementLocation != null) setLLCManagementLocation(t.llcManagementLocation);
    if (t.incomeType != null) setIncomeType(t.incomeType);
    if (t.w2SSWages != null) setW2SSWages(t.w2SSWages);
    if (t.otherSEIncome != null) setOtherSEIncome(t.otherSEIncome);
    if (typeof t.showAfterTaxEconomics === 'boolean') setShowAfterTaxEconomics(t.showAfterTaxEconomics);
  }, []);

  // ═══════ Return all state and computed values ═══════
  return {
    serializePlan, loadPlanData,
    // UI state
    showBenchmark, setShowBenchmark,

    // Market settings
    timeHorizon, setTimeHorizon,
    btcPrice, setBtcPrice,
    priceScenario, setPriceScenario,
    electricityCost, setElectricityCost,
    hostingFixed, setHostingFixed,
    poolFee, setPoolFee,
    uptimeFactor, setUptimeFactor,
    blockReward, setBlockReward,
    difficulty, setDifficulty,
    difficultyGrowth, setDifficultyGrowth,
    startingTreasury, setStartingTreasury,
    startingCash, setStartingCash,
    advisorProfile, updateAdvisorProfile,

    // DCA settings (amounts derived from mining cash flows)
    dcaBtcEnabled, setDcaBtcEnabled,
    dcaBtcPriceScenario, setDcaBtcPriceScenario,
    dcaSpEnabled, setDcaSpEnabled,
    dcaSpReturn, setDcaSpReturn,

    // Tax settings
    taxRate, setTaxRate,
    useBonusDepreciation,
    showAfterTaxEconomics, setShowAfterTaxEconomics,

    // SE Tax
    w2SSWages, setW2SSWages,
    otherSEIncome, setOtherSEIncome,

    // Tax Structure
    entityType, setEntityType,
    scorpSalaryPercent, setScorpSalaryPercent,
    filingStatus, setFilingStatus,
    qbiEnabled,
    stateConformsBonus,
    stateCode, setStateCode,
    stateTaxRate,

    // International
    ownerResidency, setOwnerResidency,
    foreignTaxRegime, setForeignTaxRegime,
    llcManagementLocation, setLLCManagementLocation,
    incomeType, setIncomeType,
    feieApplies, feieCoversIncome, feieRemaining, effectiveIncomeTaxRate, FEIE_EXCLUSION,

    // The plan's single configuration. `activeStrategy` is the long-standing
    // name every section reads; `strategy` is the same object.
    strategy, setStrategy,
    activeStrategy,
    updateStrategy,
    updateStrategyExpansion,
    PLAN_ARCHETYPES,

    // Computed values
    allCalculations,
    dcaStrategies,
    displayCalculations,
    activeCalc,
    taxBenefits,
    miningEconomics,
    operatorEconomics,
    taxStructureComparison,
    comparisonData,
    currentStats,
    sensitivityAnalysis,
    waterfallData,
    dcaVsMiningComparison,
    equivalentDCA,

    // Live market data
    marketDataMeta,
    refreshMarketData,

    // Formatters
    fmt, fBTC,
  };
}
